// Authenticated fetch wrapper with automatic token refresh
import { API_BASE, IS_CAPACITOR } from './config';

let isRefreshing = false;
let refreshPromise = null;

// --- Retry on transient gateway errors ---
// Render attaches a persistent disk to one instance at a time, so deploys have
// a brief window where the load balancer returns 502/503/504 with no backend.
// These statuses mean the request never reached our app, so retrying any
// method is safe. For network errors we only retry idempotent methods, since
// the request may have partially executed before the connection died.
const RETRY_STATUSES = new Set([502, 503, 504]);
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];

export async function fetchWithRetry(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const idempotent = method === 'GET' || method === 'HEAD';

  for (let attempt = 0; ; attempt++) {
    let res;
    try {
      res = await fetch(url, options);
    } catch (err) {
      if (idempotent && attempt < RETRY_DELAYS_MS.length) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
        continue;
      }
      throw err;
    }
    if (RETRY_STATUSES.has(res.status) && attempt < RETRY_DELAYS_MS.length) {
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
      continue;
    }
    return res;
  }
}

// --- Token storage for Capacitor (iOS WKWebView blocks third-party cookies) ---

function getStoredTokens() {
  if (!IS_CAPACITOR) return {};
  return {
    accessToken: localStorage.getItem('stellar_access_token'),
    refreshToken: localStorage.getItem('stellar_refresh_token'),
  };
}

function storeTokens(accessToken, refreshToken) {
  if (!IS_CAPACITOR) return;
  if (accessToken) localStorage.setItem('stellar_access_token', accessToken);
  if (refreshToken) localStorage.setItem('stellar_refresh_token', refreshToken);
}

function clearStoredTokens() {
  if (!IS_CAPACITOR) return;
  localStorage.removeItem('stellar_access_token');
  localStorage.removeItem('stellar_refresh_token');
}

// Save tokens from any auth response (login, register, refresh)
function handleAuthResponse(data) {
  if (IS_CAPACITOR && data.accessToken && data.refreshToken) {
    storeTokens(data.accessToken, data.refreshToken);
  }
}

// --- Core fetch wrapper ---

async function authFetch(path, options = {}) {
  const url = API_BASE + path;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Capacitor: attach access token as Authorization header
  if (IS_CAPACITOR) {
    const { accessToken } = getStoredTokens();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
  }

  const response = await fetchWithRetry(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (response.status === 401 && !path.includes('/api/auth/refresh') && !path.includes('/api/auth/login') && !path.includes('/api/auth/register') && !path.includes('/api/auth/logout')) {
    // Try refresh
    if (!isRefreshing) {
      isRefreshing = true;

      const refreshOpts = {
        method: 'POST',
        credentials: 'include',
      };

      // Capacitor: send refresh token in body
      if (IS_CAPACITOR) {
        const { refreshToken } = getStoredTokens();
        refreshOpts.headers = { 'Content-Type': 'application/json' };
        refreshOpts.body = JSON.stringify({ refreshToken });
      }

      refreshPromise = fetchWithRetry(API_BASE + '/api/auth/refresh', refreshOpts)
        .then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            handleAuthResponse(data);
            return { ok: true };
          }
          clearStoredTokens();
          return { ok: false };
        })
        .catch(() => {
          clearStoredTokens();
          return { ok: false };
        })
        .finally(() => {
          isRefreshing = false;
        });
    }

    const refreshResult = await refreshPromise;
    if (refreshResult && refreshResult.ok) {
      // Retry original request with new token
      const retryHeaders = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
      if (IS_CAPACITOR) {
        const { accessToken } = getStoredTokens();
        if (accessToken) {
          retryHeaders['Authorization'] = `Bearer ${accessToken}`;
        }
      }
      return fetchWithRetry(url, {
        ...options,
        credentials: 'include',
        headers: retryHeaders,
      });
    }

    // Refresh failed — dispatch logout
    window.dispatchEvent(new CustomEvent('auth:expired'));
    throw new Error('Session expired');
  }

  return response;
}

// --- Auth API ---

export async function register(email, password, displayName, username, inviteCode, musicService) {
  const body = { email, password, displayName, username };
  if (inviteCode) body.inviteCode = inviteCode;
  if (musicService) body.musicService = musicService;
  const res = await authFetch('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  // Clone so caller can also read .json()
  const clone = res.clone();
  try {
    const data = await clone.json();
    handleAuthResponse(data);
  } catch {}
  return res;
}

export async function checkUsername(username) {
  const res = await authFetch(`/api/auth/check-username?username=${encodeURIComponent(username)}`);
  if (!res.ok) throw new Error('Check failed');
  return res.json();
}

export function setUsername(username) {
  return authFetch('/api/auth/set-username', {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
}

export async function login(email, password, rememberMe = false) {
  const res = await authFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, rememberMe }),
  });
  const clone = res.clone();
  try {
    const data = await clone.json();
    handleAuthResponse(data);
  } catch {}
  return res;
}

export function logout() {
  const options = { method: 'POST' };
  // Capacitor: send refresh token in body so server can revoke it
  if (IS_CAPACITOR) {
    const { refreshToken } = getStoredTokens();
    options.body = JSON.stringify({ refreshToken });
  }
  clearStoredTokens();
  return authFetch('/api/auth/logout', options);
}

export function getMe() {
  return authFetch('/api/auth/me');
}

// --- Maps API ---

export function getMaps() {
  return authFetch('/api/maps');
}

export function getMap(id) {
  return authFetch(`/api/maps/${id}`);
}

export function saveMapCloud(data) {
  return authFetch('/api/maps', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function renameMapCloud(id, name) {
  return authFetch(`/api/maps/${id}/rename`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export function deleteMapCloud(id) {
  return authFetch(`/api/maps/${id}`, { method: 'DELETE' });
}

export function importMaps(maps) {
  return authFetch('/api/maps/import', {
    method: 'POST',
    body: JSON.stringify({ maps }),
  });
}

// --- Favorites API ---

export function getFavorites() {
  return authFetch('/api/favorites');
}

export function addFavorite(data) {
  return authFetch('/api/favorites', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function removeFavorite(artistName) {
  return authFetch(`/api/favorites/${encodeURIComponent(artistName)}`, {
    method: 'DELETE',
  });
}

// --- Dislikes API ---

export function getDislikes() {
  return authFetch('/api/dislikes');
}

export function addDislike(data) {
  return authFetch('/api/dislikes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function removeDislike(artistName) {
  return authFetch(`/api/dislikes/${encodeURIComponent(artistName)}`, {
    method: 'DELETE',
  });
}

// --- Known Artists API ---

export function getKnownArtists() {
  return authFetch('/api/known-artists');
}

export function addKnownArtist(data) {
  return authFetch('/api/known-artists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function removeKnownArtist(artistName) {
  return authFetch(`/api/known-artists/${encodeURIComponent(artistName)}`, {
    method: 'DELETE',
  });
}

// --- Discovered Artists API ---

export function getDiscoveredArtists() {
  return authFetch('/api/discovered-artists');
}

export function addDiscoveredArtist(data) {
  return authFetch('/api/discovered-artists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function removeDiscoveredArtist(artistName) {
  return authFetch(`/api/discovered-artists/${encodeURIComponent(artistName)}`, {
    method: 'DELETE',
  });
}

export function getDiscoveredConnections() {
  return authFetch('/api/discovered-artists/connections');
}

// --- Universe API ---

export function getUniverse() {
  return authFetch('/api/universe');
}

export function triggerUniverseCompute() {
  return authFetch('/api/universe/compute', { method: 'POST' });
}

export function getUniverseStatus() {
  return authFetch('/api/universe/status');
}

export function getMusicalDna() {
  return authFetch('/api/universe/musical-dna');
}

// --- Shared Playlists API ---

export function createSharedPlaylist(data) {
  return authFetch('/api/playlists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Apple Music API ---

// Public feature flag: whether the server has MusicKit credentials. The
// frontend hides all Apple Music connect/export UI when this is false.
export function getAppleMusicStatus() {
  return fetchWithRetry(`${API_BASE}/api/apple-music/status`);
}

export function getAppleMusicDeveloperToken() {
  return authFetch('/api/apple-music/developer-token');
}

// Account-stored Music User Token, so connecting on one device connects all.
export function getStoredAppleMusicUserToken() {
  return authFetch('/api/apple-music/user-token');
}

export function saveAppleMusicUserToken(token) {
  return authFetch('/api/apple-music/user-token', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export function deleteAppleMusicUserToken() {
  return authFetch('/api/apple-music/user-token', { method: 'DELETE' });
}

// --- Profile API ---

export function updateProfile({ displayName, email }) {
  return authFetch('/api/auth/profile', {
    method: 'PUT',
    body: JSON.stringify({ displayName, email }),
  });
}

export function uploadAvatar(base64Data) {
  return authFetch('/api/auth/avatar', {
    method: 'POST',
    body: JSON.stringify({ avatar: base64Data }),
  });
}

export function deleteAvatar() {
  return authFetch('/api/auth/avatar', { method: 'DELETE' });
}

export function getAvatarUrl(userId) {
  return `${API_BASE}/api/auth/avatar/${userId}`;
}

// --- Milestone API ---

export function updateMilestone(milestone) {
  return authFetch('/api/auth/milestone', {
    method: 'POST',
    body: JSON.stringify({ milestone }),
  });
}

export function updateMusicService(service) {
  return authFetch('/api/auth/music-service', {
    method: 'POST',
    body: JSON.stringify({ service }),
  });
}

export function markFirstDiscoveryShown() {
  return authFetch('/api/auth/first-discovery-shown', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function markFriendInviteShown() {
  return authFetch('/api/auth/friend-invite-shown', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function markActionPillsCalloutShown() {
  return authFetch('/api/auth/action-pills-callout-shown', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function recordArtistPanelOpened() {
  return authFetch('/api/auth/artist-panel-opened', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// --- Account Deletion API ---

export function deleteAccount(password) {
  return authFetch('/api/auth/account', {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  });
}

// --- Password Reset API (no auth needed) ---

export function forgotPassword(email) {
  return fetchWithRetry(API_BASE + '/api/auth/forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export function resetPassword(token, password) {
  return fetchWithRetry(API_BASE + '/api/auth/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
}

export function getSharedPlaylist(id) {
  return fetchWithRetry(`${API_BASE}/api/playlists/${id}`).then((res) => {
    if (!res.ok) throw new Error('Playlist not found');
    return res.json();
  });
}

// --- Shared Galaxy Maps API ---

export function createGalaxyShare(data) {
  return authFetch('/api/galaxy-shares', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getGalaxyShare(id) {
  return fetchWithRetry(`${API_BASE}/api/galaxy-shares/${id}`).then((res) => {
    if (!res.ok) throw new Error('Galaxy not found');
    return res.json();
  });
}

// --- Shared Universe Maps API ---

export function createUniverseShare(data) {
  return authFetch('/api/universe-shares', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getUniverseShare(id) {
  return fetchWithRetry(`${API_BASE}/api/universe-shares/${id}`).then((res) => {
    if (!res.ok) throw new Error('Universe not found');
    return res.json();
  });
}

// --- Friends API ---

export function getFriends() {
  return authFetch('/api/friends');
}

export function getFriendRequests() {
  return authFetch('/api/friends/requests');
}

export function getSentRequests() {
  return authFetch('/api/friends/sent');
}

export function sendFriendRequest(username) {
  return authFetch('/api/friends/request', {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
}

export function acceptFriendRequest(userId) {
  return authFetch('/api/friends/accept', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

export function rejectFriendRequest(userId) {
  return authFetch('/api/friends/reject', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

export function removeFriend(userId) {
  return authFetch(`/api/friends/${userId}`, { method: 'DELETE' });
}

export function favoriteFriend(userId) {
  return authFetch('/api/friends/favorite', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

export function unfavoriteFriend(userId) {
  return authFetch(`/api/friends/favorite/${userId}`, { method: 'DELETE' });
}

export function searchUsers(query) {
  return authFetch(`/api/friends/search?q=${encodeURIComponent(query)}`);
}

export function getFriendArtists(friendId) {
  return authFetch(`/api/friends/${friendId}/artists`);
}

// --- Collision API ---

export function getCollision(friendId) {
  return authFetch(`/api/collision/${friendId}`);
}

export function triggerCollisionCompute(friendId) {
  return authFetch(`/api/collision/${friendId}/compute`, { method: 'POST' });
}

export function getCollisionStatus(friendId) {
  return authFetch(`/api/collision/${friendId}/status`);
}

// --- Invites API ---

export function createInvite() {
  return authFetch('/api/invites', { method: 'POST' });
}

export function getInvites() {
  return authFetch('/api/invites');
}

export function deleteInvite(code) {
  return authFetch(`/api/invites/${encodeURIComponent(code)}`, { method: 'DELETE' });
}

export function getInviteInfo(code) {
  return fetchWithRetry(`${API_BASE}/api/invites/info/${encodeURIComponent(code)}`).then((res) => {
    if (!res.ok) return res.json().then((d) => { throw new Error(d.error || 'Invalid invite'); });
    return res.json();
  });
}

// --- Notifications API ---

export function registerPushToken(token, platform, tzOffsetMinutes) {
  return authFetch('/api/notifications/register', {
    method: 'POST',
    body: JSON.stringify({ token, platform, tzOffsetMinutes }),
  });
}

export function unregisterPushToken(token) {
  return authFetch('/api/notifications/unregister', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export function getNotificationPrefs() {
  return authFetch('/api/notifications/preferences');
}

export function updateNotificationPrefs(type, enabled, frequency) {
  return authFetch('/api/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify({ type, enabled, frequency }),
  });
}

export function markNotificationPrompted() {
  return authFetch('/api/notifications/prompted', { method: 'POST' });
}

export function sendTestNotification() {
  return authFetch('/api/notifications/test', { method: 'POST' });
}
